class CartDrawer extends HTMLElement {
  constructor() {
    super();

    const events = ["cart-drawer-rendered", "cart-after-update"];

    events.forEach((event) => {
      document.addEventListener(event, () => this.updateDrawerMinHeight());
    });

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this),
    );
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // Add-to-cart only renders the tiny cart-icon-bubble section so adds stay
    // fast; this drawer is refreshed lazily instead. If an add happened since
    // it was last rendered, bring it up to date before showing it.
    if (this.isStale || this.refreshPromise) {
      (this.refreshPromise || this.refresh()).then(() => {
        // The in-flight refresh may have started before the most recent add,
        // so run one more pass if the cart moved again while it was loading.
        if (this.isStale) {
          this.refresh().then(() => this.revealDrawer());
          return;
        }
        this.revealDrawer();
      });
      return;
    }

    this.revealDrawer();
  }

  revealDrawer() {
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.dispatchEvent(new CustomEvent("cart-drawer-rendered"));

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  // Flags the drawer as out of date after an add handled by the popup, and
  // warms a refresh in the background so opening the cart still feels instant.
  markStale() {
    this.isStale = true;
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      if (this.isStale) this.refresh();
    }, 300);
  }

  // Re-renders the drawer contents from the cart-drawer section without
  // opening it. Returns a promise so open() can await an in-flight refresh.
  refresh() {
    clearTimeout(this.refreshTimer);

    // Already loading: that request may predate the latest add, so queue one
    // more pass to run when it finishes rather than starting a parallel fetch.
    if (this.refreshPromise) {
      this.refreshQueued = true;
      return this.refreshPromise;
    }

    const root =
      (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";

    this.refreshPromise = fetch(`${root}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const newDrawer = new DOMParser()
          .parseFromString(responseText, "text/html")
          .querySelector("#CartDrawer");
        const currentDrawer = this.querySelector("#CartDrawer");

        if (newDrawer && currentDrawer) {
          currentDrawer.innerHTML = newDrawer.innerHTML;
          this.classList.remove("is-empty");
          this.querySelector(".drawer__inner")?.classList.remove("is-empty");

          // The overlay lives inside #CartDrawer, so replacing the markup
          // above drops the click-to-close listener bound in the constructor.
          const overlay = this.querySelector("#CartDrawer-Overlay");
          if (overlay) overlay.addEventListener("click", () => this.close());

          if (typeof updateUTMminicart === "function") updateUTMminicart();
          document.dispatchEvent(new CustomEvent("cart-drawer-rendered"));
        }

        this.isStale = false;
      })
      .catch((error) => console.error("Cart drawer refresh failed", error))
      .finally(() => {
        this.refreshPromise = null;
        if (this.refreshQueued) {
          this.refreshQueued = false;
          this.refresh();
        }
      });

    return this.refreshPromise;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState, { shouldOpen = true } = {}) {
    updateUTMminicart();
    // Contents come straight from the response here, so any pending
    // lazy refresh is redundant.
    this.isStale = false;
    clearTimeout(this.refreshTimer);
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    document.dispatchEvent(new CustomEvent("cart-drawer-rendered"));

    if (shouldOpen) {
      setTimeout(() => {
        this.querySelector("#CartDrawer-Overlay").addEventListener(
          "click",
          this.close.bind(this),
        );
        this.open();
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  updateDrawerMinHeight() {
    const drawerInnerContent = this.querySelector(".drawer__inner-content");
    const drawerFooter = this.querySelector(".drawer__footer");

    drawerInnerContent.style.minHeight = `calc(100vh - ${drawerFooter.offsetHeight ? drawerFooter.offsetHeight : 197}px)`;
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
